<template>
  <FileReport />
  <TutorHeader />
  <div class="tutor-page-wrapper">
    <!-- Messages Content -->
    <div class="flex-1 pt-32 px-3 sm:px-4 md:px-6 pb-2">
      <div class="h-full w-full max-w-7xl mx-auto">
        <!-- Loading State with Skeleton Screen -->
        <MessagingLayoutSkeleton v-if="loading || cometChatLoading" :showChat="false" />

        <!-- Account Not Verified State -->
        <div v-else-if="!canMessage" class="flex items-center justify-center min-h-screen">
          <div class="text-center max-w-md mx-auto">
            <div
              class="w-24 h-24 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <svg
                class="w-12 h-12 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                ></path>
              </svg>
            </div>
            <h3 class="text-2xl font-semibold text-gray-900 mb-3">Profile Verification Required</h3>
            <p class="text-gray-600 text-lg mb-4">
              Your tutor profile is not yet verified. Messaging is temporarily disabled until your
              profile is approved by an administrator.
            </p>
            <p class="text-gray-500 text-sm">
              Please wait for an administrator to review and approve your tutor profile.
            </p>
          </div>
        </div>

        <!-- No Students State -->
        <div
          v-else-if="!loading && students.length === 0"
          class="flex items-center justify-center min-h-screen"
        >
          <div class="text-center">
            <div
              class="w-24 h-24 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <svg
                class="w-12 h-12 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                ></path>
              </svg>
            </div>
            <h3 class="text-2xl font-semibold text-gray-900 mb-3">No Students Available</h3>
            <p class="text-gray-600 text-lg">
              No students have initiated conversations with you yet.
            </p>
          </div>
        </div>

        <!-- Main Messages Interface -->
        <div v-else class="h-full">
          <!-- Desktop Layout -->
          <div
            class="hidden lg:flex h-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 messaging-container-height"
          >
            <!-- Left Sidebar - Contact List -->
            <div class="w-80 min-w-80 bg-gradient-to-b from-white to-gray-50 flex flex-col">
              <!-- Header -->
              <div class="p-6 border-b border-gray-200 bg-white">
                <h1 class="text-2xl font-bold text-gray-900 mb-2">Messages</h1>
                <p class="text-gray-600">Connect with your students</p>
              </div>

              <!-- Search Bar -->
              <div class="p-4 bg-white">
                <div class="relative">
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search students..."
                    class="w-full pl-12 pr-4 py-3 text-gray-700 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-200 placeholder-gray-500"
                  />
                  <svg
                    class="absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              <!-- Contact List -->
              <div class="flex-1 overflow-y-auto bg-gradient-to-b from-white to-gray-50">
                <ContactList
                  :contacts="filteredStudents"
                  :selectedContact="selectedStudent"
                  @select-contact="selectStudent"
                />
              </div>
            </div>

            <!-- Right Panel - Chat Window -->
            <div class="flex-1 flex flex-col bg-white min-h-0 max-h-full">
              <ChatWindow
                v-if="selectedStudent"
                :key="`chat-${selectedStudent.id}-${selectedStudentMessages.length}`"
                :contact="selectedStudent"
                :messages="selectedStudentMessages"
                :userType="userType"
                :isCometChatLoading="cometChatLoading"
                @send-message="sendMessage"
              />
              <!-- No empty state - first student is auto-selected -->
            </div>
          </div>

          <!-- Mobile Layout -->
          <div class="lg:hidden h-full">
            <!-- Contact List View (Mobile) -->
            <div
              v-if="!selectedStudent"
              class="h-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 messaging-container-height"
            >
              <!-- Header -->
              <div class="p-6 border-b border-gray-200 bg-white">
                <h1 class="text-2xl font-bold text-gray-900 mb-2">Messages</h1>
                <p class="text-gray-600">Connect with your students</p>
              </div>

              <!-- Search Bar -->
              <div class="p-4 bg-white">
                <div class="relative">
                  <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search students..."
                    class="w-full pl-12 pr-4 py-3 text-gray-700 bg-gray-50 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-200 placeholder-gray-500"
                  />
                  <svg
                    class="absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              <!-- Contact List -->
              <div class="flex-1 overflow-y-auto bg-gradient-to-b from-white to-gray-50">
                <ContactList
                  :contacts="filteredStudents"
                  :selectedContact="selectedStudent"
                  @select-contact="selectStudent"
                />
              </div>
            </div>

            <!-- Chat View (Mobile) -->
            <div
              v-else
              class="h-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 messaging-container-height"
            >
              <ChatWindow
                :key="`chat-mobile-${selectedStudent.id}-${selectedStudentMessages.length}`"
                :contact="selectedStudent"
                :messages="selectedStudentMessages"
                :userType="userType"
                :isCometChatLoading="cometChatLoading"
                @send-message="sendMessage"
                @back="goBack"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, onActivated, nextTick } from "vue";
import { format, parse } from "date-fns";
import {
  ensureInitializedAndLoggedIn,
  addMessageListener,
  removeMessageListener,
  sendTextMessage,
  fetchUserMessages,
  createOrUpdateUser,
  addUserListener,
  removeUserListener,
  getLoggedInUser,
  getBatchUserStatuses,
  fetchUnreadMessageCountMap,
  markConversationAsReadInCometChat,
  markMessageAsRead,
} from "@/services/cometchat";
import { getCurrentUserProfile, formatTutorName } from "@/services/firebase";
import { messagingPermissionService } from "@/services/messagingPermissionService";
import { logInfo, logError, logWarn, logDebug } from "@/utils/secureLogger";
import { useRoute } from "vue-router";
import { useMessagingStore } from "@/stores/messagingStore";
import { useMessagingSync } from "@/composables/useMessagingSync";
import { storeToRefs } from "pinia";
import { useTutorStatus } from "@/composables/useTutorStatus";

import ContactList from "@/components/Student-Tutor/Messages/ContactList.vue";
import ChatWindow from "@/components/Student-Tutor/Messages/ChatWindow.vue";
import MessagingLayoutSkeleton from "@/components/Student-Tutor/Messages/MessagingLayoutSkeleton.vue";
import ChatWindowSkeleton from "@/components/Student-Tutor/Messages/ChatWindowSkeleton.vue";
import TutorHeader from "@/components/TutorHeader.vue";
import FileReport from "@/components/FileReport.vue";

export default {
  name: "TutorMessagesView",
  components: {
    ContactList,
    ChatWindow,
    MessagingLayoutSkeleton,
    ChatWindowSkeleton,
    TutorHeader,
    FileReport,
  },
  props: {
    // User type to determine if this is a student or tutor view
    userType: {
      type: String,
      required: false,
      default: "tutor",
      validator: (value) => ["student", "tutor"].includes(value),
    },
  },
  setup(props) {
    const route = useRoute();
    const searchQuery = ref("");
    const selectedStudent = ref(null);
    const currentUser = ref(null);
    const students = ref([]);
    const loading = ref(true);
    const cometChatLoading = ref(false);

    // Tutor status composable
    const { canMessage, status, isLoading: statusLoading, error: statusError } = useTutorStatus();

    // Messaging store and sync
    const messagingStore = useMessagingStore();
    const { unreadConversationCount } = storeToRefs(messagingStore);
    const { broadcastConversationRead, broadcastUnreadCountsUpdate } = useMessagingSync('tutor');

    // Messages storage - will be populated from CometChat
    const messages = ref({});

    // Helper function to convert time string to Date object
    const convertTimeStringToDate = (timeString) => {
      // Handle "Today" case
      if (timeString.includes("Today")) {
        const today = new Date();
        const time = timeString.split(" at ")[1];
        const [hour, minute, period] = time.match(/(\d+):(\d+)\s*(AM|PM)/).slice(1);
        today.setHours(
          period === "PM" && hour !== "12"
            ? parseInt(hour) + 12
            : period === "AM" && hour === "12"
            ? 0
            : parseInt(hour),
          parseInt(minute),
          0,
          0
        );
        return today;
      }

      // Try to parse the time string
      try {
        return parse(timeString, "h:mm a", new Date());
      } catch (error) {
        // If parsing fails, return current date (fallback)
        return new Date();
      }
    };

    const sortedStudents = computed(() => {
      return [...students.value].sort((a, b) => {
        // Use lastMessageTimestamp (Date object) if available, otherwise fallback to lastMessageTime string
        const timeA =
          a.lastMessageTimestamp ||
          (a.lastMessageTime ? new Date(convertTimeStringToDate(a.lastMessageTime)) : null);
        const timeB =
          b.lastMessageTimestamp ||
          (b.lastMessageTime ? new Date(convertTimeStringToDate(b.lastMessageTime)) : null);

        // Sort by last message time (most recent first)
        if (timeA && !timeB) return -1;
        if (!timeA && timeB) return 1;
        if (!timeA && !timeB) return a.name.localeCompare(b.name);

        // Compare timestamps
        return timeB.getTime() - timeA.getTime();
      });
    });

    const filteredStudents = computed(() => {
      // First, filter out the current user (tutor cannot message themselves)
      const currentUserId = currentUser.value?.uid?.toLowerCase();
      const studentsWithoutSelf = sortedStudents.value.filter((student) => {
        const studentId = String(student.id || '').toLowerCase();
        return studentId !== currentUserId;
      });
      
      // Then apply search filter if there's a search query
      if (!searchQuery.value) return studentsWithoutSelf;
      return studentsWithoutSelf.filter((student) =>
        student.name.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    });

    // Helper function to parse time strings with better date handling
    const parseTimeString = (timeString) => {
      if (!timeString) return null;

      try {
        const now = new Date();

        // If it's a full date string, parse it directly
        if (timeString.includes("/") || timeString.includes("-")) {
          const parsedDate = new Date(timeString);
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate;
          }
        }

        // Handle time-only strings (e.g., "2:30 PM")
        if (timeString.includes(":") && (timeString.includes("AM") || timeString.includes("PM"))) {
          const [time, period] = timeString.split(" ");
          const [hours, minutes] = time.split(":").map(Number);

          const date = new Date(now);
          date.setHours(
            period === "AM" ? (hours === 12 ? 0 : hours) : hours === 12 ? 12 : hours + 12
          );
          date.setMinutes(minutes);
          date.setSeconds(0);
          date.setMilliseconds(0);

          // If the time is in the future, assume it's from yesterday
          if (date > now) {
            date.setDate(date.getDate() - 1);
          }

          return date;
        }

        // Handle 24-hour format (e.g., "14:30")
        if (timeString.includes(":") && !timeString.includes("AM") && !timeString.includes("PM")) {
          const [hours, minutes] = timeString.split(":").map(Number);

          const date = new Date(now);
          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(0);
          date.setMilliseconds(0);

          // If the time is in the future, assume it's from yesterday
          if (date > now) {
            date.setDate(date.getDate() - 1);
          }

          return date;
        }

        return null;
      } catch (error) {
        logWarn("Failed to parse time string", { timeString, error });
        return null;
      }
    };

    const selectedStudentMessages = computed(() => {
      if (!selectedStudent.value) return [];
      return messages.value[selectedStudent.value.id] || [];
    });

    // Sync unread counts from CometChat to student contact objects
    // Uses messaging store which tracks explicitly read conversations
    const syncUnreadCountsToContacts = (unreadMap) => {
      logInfo('🔄 Syncing unread counts to contacts', {
        unreadMapSize: Object.keys(unreadMap).length,
        explicitlyReadSize: messagingStore.explicitlyRead.size,
        studentsCount: students.value.length,
        unreadMapEntries: Object.entries(unreadMap).map(([id, count]) => ({ 
          id: id.substring(0, 8) + '***', 
          count 
        }))
      });
      
      students.value.forEach((student) => {
        const studentIdLower = student.id.toLowerCase();
        const studentKey = student.id.toLowerCase();
        // Check both original case and lowercase versions
        const cometchatCount = unreadMap[student.id] || unreadMap[studentIdLower] || 0;
        
        // If this is the currently selected student, always preserve 0 (it's open, so it's read)
        if (selectedStudent.value && selectedStudent.value.id === student.id) {
          if (student.unreadCount !== 0) {
            logInfo('📭 Clearing unread for currently selected student', {
              studentId: student.id.substring(0, 8) + '***',
              studentName: student.name
            });
            student.unreadCount = 0;
          }
          messagingStore.markAsRead(studentKey);
          return;
        }
        
        // If we've explicitly marked this as read, preserve 0 unless CometChat shows new messages
        if (messagingStore.explicitlyRead.has(studentKey)) {
          logDebug('💬 Processing explicitly read conversation in sync', {
            studentId: student.id.substring(0, 8) + '***',
            currentLocalCount: student.unreadCount,
            cometchatCount,
            action: cometchatCount > 0 ? 'new messages detected' : 'preserving 0'
          });
          
          // Only update if CometChat shows new messages (count increased)
          if (cometchatCount > 0) {
            logInfo('📨 Setting unread count for explicitly read conversation (NEW MESSAGES)', {
              studentId: student.id.substring(0, 8) + '***',
              studentName: student.name,
              count: cometchatCount
            });
            student.unreadCount = cometchatCount;
            messagingStore.explicitlyRead.delete(studentKey); // New messages, remove from read set
          } else {
            // Keep it at 0, CometChat hasn't updated yet or confirms it's read
            // Force it to 0 to prevent any flicker
            if (student.unreadCount !== 0) {
              student.unreadCount = 0;
            }
          }
          return;
        }
        
        // For conversations NOT explicitly marked as read, update normally from CometChat
        // If CometChat shows unread messages, update the count
        if (cometchatCount > 0) {
          logInfo('📨 Setting unread count for conversation', {
            studentId: student.id.substring(0, 8) + '***',
            studentName: student.name,
            previousCount: student.unreadCount,
            newCount: cometchatCount
          });
          student.unreadCount = cometchatCount;
        } 
        // If CometChat confirms 0 but we have a local count, clear it
        else if (cometchatCount === 0 && student.unreadCount > 0) {
          logInfo('📭 Clearing unread count (CometChat confirms 0)', {
            studentId: student.id.substring(0, 8) + '***',
            studentName: student.name,
            previousCount: student.unreadCount
          });
          student.unreadCount = 0;
        }
      });
      
      // Log final state after sync
      const studentsWithUnread = students.value.filter(s => s.unreadCount > 0);
      logInfo('✅ Sync complete - Final state', {
        totalStudents: students.value.length,
        studentsWithUnread: studentsWithUnread.length,
        unreadDetails: studentsWithUnread.map(s => ({
          name: s.name,
          id: s.id.substring(0, 8) + '***',
          count: s.unreadCount
        }))
      });
      
      // Trigger reactivity
      students.value = [...students.value];
    };

    const selectStudent = async (student) => {
      // Prevent tutors from selecting themselves
      const currentUserId = currentUser.value?.uid?.toLowerCase();
      const studentId = String(student.id || '').toLowerCase();
      if (currentUserId && studentId === currentUserId) {
        logWarn("Cannot select yourself as a contact");
        return;
      }
      
      selectedStudent.value = student;

      logInfo('📖 Opening conversation - marking as read immediately', { 
        studentId: student.id.substring(0, 8) + '***',
        studentName: student.name,
        currentUnreadCount: student.unreadCount
      });

      // Mark messages as read when selecting a student
      if (messages.value[student.id]) {
        messages.value[student.id].forEach((msg) => {
          if (msg.sender !== props.userType) msg.isRead = true;
        });
      }
      // IMMEDIATELY reset unread count on the student object in the array
      // This ensures the UI updates right away when opening the conversation
      const studentInArray = students.value.find((s) => s.id === student.id);
      if (studentInArray) {
        studentInArray.unreadCount = 0;
      }
      student.unreadCount = 0;
      
      // Track this conversation as explicitly marked as read BEFORE any async operations
      // This prevents sync from overwriting the 0 count
      const studentKey = student.id.toLowerCase();
      messagingStore.markAsRead(studentKey);
      broadcastConversationRead(studentKey);
      
      // Save timestamp to protect against ghost conversations
      try {
        let recentlyMarkedRead = {};
        const stored = localStorage.getItem('elevateED_recently_marked_read_tutor');
        if (stored) {
          recentlyMarkedRead = JSON.parse(stored);
        }
        recentlyMarkedRead[studentKey] = Date.now();
        localStorage.setItem('elevateED_recently_marked_read_tutor', JSON.stringify(recentlyMarkedRead));
        
        logDebug('💾 Saved recently marked read timestamp for ghost protection', {
          studentId: student.id.substring(0, 8) + '***',
          timestamp: Date.now()
        });
      } catch (error) {
        logWarn('Failed to save recently marked read timestamp', { error });
      }
      
      logInfo('✅ Conversation marked as read in store and broadcast', {
        studentId: student.id.substring(0, 8) + '***',
        explicitlyReadSize: messagingStore.explicitlyRead.size
      });
      
      // Force reactivity update IMMEDIATELY so the UI shows 0 right away
      students.value = [...students.value];
      
      logInfo('🔔 Dispatched unread-state-changed event to sync with tutor header', {
        studentId: student.id.substring(0, 8) + '***'
      });
      
      // Mark conversation as read in CometChat
      await markConversationAsReadInCometChat(student.id);
      
      // Event-driven updates: unread counts update automatically via message listeners
      // No polling needed - the messaging store tracks explicitly read conversations

      // Load chat history on-demand if not already loaded
      // Don't reload if messages exist (to preserve locally sent messages)
      if (!messages.value[student.id] || messages.value[student.id].length === 0) {
        try {
          // Ensure CometChat is ready before attempting to fetch history
          await ensureCometChatReady();

          // Show loading state for this specific student
          student.isLoadingHistory = true;

          // Add retry logic for fetching messages on first login
          let history = [];
          let fetchAttempts = 0;
          const maxFetchAttempts = 3;
          
          while (fetchAttempts < maxFetchAttempts) {
            try {
              fetchAttempts++;
              logDebug(`Fetching conversation history (attempt ${fetchAttempts}/${maxFetchAttempts})`, {
                studentId: student.id.substring(0, 8) + '***'
              });
              
              history = await fetchUserMessages(String(student.id), 50);
              logInfo(`✅ Successfully fetched ${history.length} messages`, {
                studentId: student.id.substring(0, 8) + '***',
                messageCount: history.length,
                attempt: fetchAttempts
              });
              break; // Success, exit retry loop
            } catch (fetchError) {
              logWarn(`Fetch attempt ${fetchAttempts} failed`, {
                error: fetchError,
                errorCode: fetchError?.code,
                errorMessage: fetchError?.message,
                studentId: student.id.substring(0, 8) + '***'
              });
              
              // If not the last attempt, wait before retrying
              if (fetchAttempts < maxFetchAttempts) {
                // Progressive delay: 500ms, 1000ms
                const retryDelay = 500 * fetchAttempts;
                logDebug(`Waiting ${retryDelay}ms before retry...`);
                await new Promise(resolve => setTimeout(resolve, retryDelay));
                
                // Re-ensure CometChat is ready before retry
                try {
                  await ensureCometChatReady();
                } catch (readyError) {
                  logWarn('CometChat not ready on retry', readyError);
                }
              } else {
                // Last attempt failed, throw the error
                throw fetchError;
              }
            }
          }

          const normalized = history
            .filter((m) => typeof m.getText === "function")
            .map((m) => ({
              id: m.getId?.() || Date.now(),
              sender:
                m.getSender?.()?.getUid?.()?.toLowerCase() ===
                String(currentUser.value?.uid)?.toLowerCase()
                  ? "tutor"
                  : "student",
              message: m.getText?.() || "",
              timestamp: new Date(m.getSentAt() * 1000).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              }),
              sentAt: m.getSentAt?.() || Date.now() / 1000, // Store Unix timestamp for date grouping
              isRead: true,
            }));
          messages.value[student.id] = normalized;

          // Force reactivity update for messages
          messages.value = { ...messages.value };

          // Mark messages as read in CometChat
          const lastMessage = normalized[normalized.length - 1];
          if (lastMessage && lastMessage.sender !== 'tutor') {
            await markMessageAsRead(lastMessage.id, student.id);
          }
          
          // Ensure unread count is 0 after loading history (should already be 0 from selectStudent)
          const studentInArrayAfterLoad = students.value.find((s) => s.id === student.id);
          if (studentInArrayAfterLoad) {
            studentInArrayAfterLoad.unreadCount = 0;
          }
          student.unreadCount = 0;
          
          // Force reactivity update
          students.value = [...students.value];

          // Update last message info
          if (normalized.length > 0) {
            const lastMessage = normalized[normalized.length - 1];
            student.lastMessage = lastMessage.message;
            student.lastMessageTime = lastMessage.timestamp;
          }

          student.isLoadingHistory = false;
        } catch (e) {
          logError("❌ Failed to load chat history after all retries", {
            error: e,
            errorCode: e?.code,
            errorMessage: e?.message,
            studentId: student.id.substring(0, 8) + '***'
          });
          student.isLoadingHistory = false;
        }
      }
    };

    const goBack = () => {
      selectedStudent.value = null;
    };

    const sendMessage = async (message) => {
      if (!selectedStudent.value) return;

      // Prevent tutors from messaging themselves
      const currentUserId = currentUser.value?.uid?.toLowerCase();
      const studentId = String(selectedStudent.value.id || '').toLowerCase();
      if (currentUserId && studentId === currentUserId) {
        logWarn("Cannot send message to yourself");
        return;
      }

      // Validate message length
      if (message.length > 10000) {
        logWarn("Message exceeds 10,000 character limit");
        return;
      }

      // ⚡ OPTIMISTIC UI: Add message immediately to UI
      const now = new Date();
      const timestamp = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      const tempId = `temp-${Date.now()}`; // Temporary ID for optimistic message
      const optimisticMessage = {
        id: tempId,
        sender: props.userType,
        message: message,
        timestamp,
        sentAt: now.getTime() / 1000,
        isRead: false,
        isPending: true, // Flag to show pending state
      };

      // Add to UI immediately
      if (!messages.value[selectedStudent.value.id]) {
        messages.value[selectedStudent.value.id] = [];
      }
      messages.value[selectedStudent.value.id].push(optimisticMessage);
      
      // Update student's last message info
      selectedStudent.value.lastMessage = message;
      selectedStudent.value.lastMessageTime = timestamp;
      selectedStudent.value.lastMessageTimestamp = now;

      // Force reactivity
      const studentIdValue = selectedStudent.value.id;
      messages.value[studentIdValue] = [...messages.value[studentIdValue]];
      messages.value = { ...messages.value };

      // Update in students array
      const studentInList = students.value.find((s) => s.id === selectedStudent.value.id);
      if (studentInList) {
        studentInList.lastMessage = message;
        studentInList.lastMessageTime = timestamp;
        studentInList.lastMessageTimestamp = now;
      }
      students.value = [...students.value];

      await nextTick();

      try {
        // Ensure CometChat is ready before sending
        await ensureCometChatReady();

        // Send message through CometChat
        logInfo("Sending message to student", {
          studentId: selectedStudent.value.id.substring(0, 8) + "***",
          messageLength: message.length,
        });

        const sentMessage = await sendTextMessage(String(selectedStudent.value.id), message);
        logInfo("✅ Message sent successfully via CometChat");

        // Replace optimistic message with confirmed one
        const messageIndex = messages.value[studentIdValue].findIndex(m => m.id === tempId);
        if (messageIndex !== -1) {
          messages.value[studentIdValue][messageIndex] = {
            id: sentMessage.getId?.() || Date.now(),
            sender: props.userType,
            message: message,
            timestamp,
            sentAt: now.getTime() / 1000,
            isRead: false,
            isPending: false, // No longer pending
          };
        }

        // Force reactivity
        messages.value[studentIdValue] = [...messages.value[studentIdValue]];
        messages.value = { ...messages.value };
        students.value = [...students.value];

        await nextTick();
      } catch (e) {
        logError("❌ Failed to send message via CometChat", e);

        // ⚠️ OPTIMISTIC UI ERROR: Mark message as failed
        const messageIndex = messages.value[studentIdValue]?.findIndex(m => m.id === tempId);
        if (messageIndex !== -1) {
          messages.value[studentIdValue][messageIndex].isPending = false;
          messages.value[studentIdValue][messageIndex].isFailed = true; // Mark as failed
        }

        // Force reactivity to show error state
        messages.value[studentIdValue] = [...messages.value[studentIdValue]];
        messages.value = { ...messages.value };

        // Provide more specific error messages
        let errorMessage = "Failed to send message. ";

        if (e?.code === "ERR_UID_NOT_FOUND") {
          errorMessage += "Student not found in chat system. Please refresh and try again.";
        } else if (e?.code === "ERR_NOT_LOGGED_IN") {
          errorMessage += "You are not logged into the chat system. Please refresh the page.";
        } else if (e?.message?.includes("401")) {
          errorMessage += "Authentication error. Please refresh the page and try again.";
        } else {
          errorMessage += "Please check your connection and try again.";
        }

        logError("CometChat error details", e);
        alert(errorMessage);
      }
    };

    // Helper function to create CometChat users in background
    const createCometChatUsersInBackground = async (studentsList) => {
      try {
        const batchSize = 5; // Increased batch size for better performance
        for (let i = 0; i < studentsList.length; i += batchSize) {
          const batch = studentsList.slice(i, i + batchSize);
          await Promise.all(
            batch.map(async (student) => {
              try {
                await createOrUpdateUser(student.id, student.name, student.avatar);
              } catch (error) {
                logWarn(`Failed to create CometChat user for student ${student.id}`, error);
              }
            })
          );

          // Reduced delay between batches
          if (i + batchSize < studentsList.length) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }
        logInfo("CometChat users created in background");
      } catch (error) {
        logError("Error creating CometChat users in background", error);
      }
    };

    // Helper function to load initial conversations
    const loadInitialConversations = async (studentsList) => {
      logInfo('🔄 Starting to load initial conversations', { totalStudents: studentsList.length });
      
      try {
        // Ensure CometChat is ready before attempting to fetch messages
        try {
          await ensureCometChatReady();
          logInfo('✅ CometChat ready for loading conversations');
        } catch (cometChatError) {
          logError("Cannot load conversations - CometChat initialization failed", { 
            error: cometChatError 
          });
          // Don't throw - allow the UI to work even without message history
          return;
        }

        let successCount = 0;
        let failCount = 0;

        // Load conversations in parallel for faster loading (but limit concurrency)
        const batchSize = 2; // ⚡ Performance: Load only 2 conversations initially for faster startup
        for (let i = 0; i < studentsList.length; i += batchSize) {
          const batch = studentsList.slice(i, i + batchSize);
          
          await Promise.all(
            batch.map(async (student) => {
              try {
                // Retry logic for fetching messages
                let history = [];
                let attempts = 0;
                const maxAttempts = 2;

                while (attempts < maxAttempts) {
                  try {
                    attempts++;
                    history = await fetchUserMessages(String(student.id), 20); // Load last 20 messages
                    break; // Success, exit retry loop
                  } catch (fetchError) {
                    if (attempts < maxAttempts) {
                      logWarn(`Retry loading conversation for ${student.name}`, {
                        attempt: attempts,
                        studentId: student.id.substring(0, 8) + '***'
                      });
                      await new Promise(resolve => setTimeout(resolve, 500 * attempts));
                    } else {
                      throw fetchError;
                    }
                  }
                }

                if (history.length > 0) {
                  const lastMessage = history[history.length - 1];
                  const lastMessageDate = new Date(lastMessage.getSentAt() * 1000);
                  
                  student.lastMessage = lastMessage.getText?.() || "";
                  student.lastMessageTime = lastMessageDate.toLocaleTimeString(
                    "en-US",
                    {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    }
                  );
                  student.lastMessageTimestamp = lastMessageDate; // Store Date object for sorting

                  // Pre-load messages for this student
                  const normalizedHistory = history
                    .filter((m) => typeof m.getText === "function")
                    .map((m) => ({
                      id: m.getId?.() || Date.now(),
                      sender:
                        m.getSender?.()?.getUid?.()?.toLowerCase() ===
                        String(currentUser.value?.uid)?.toLowerCase()
                          ? "tutor"
                          : "student",
                      message: m.getText?.() || "",
                      timestamp: new Date(m.getSentAt() * 1000).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      }),
                      sentAt: m.getSentAt?.() || Date.now() / 1000, // Store Unix timestamp for date grouping
                      isRead: true,
                    }));
                  
                  // Only set messages if they don't already exist (to preserve locally sent messages)
                  if (!messages.value[student.id]) {
                    messages.value[student.id] = normalizedHistory;
                  }
                  successCount++;
                  
                  logDebug(`✅ Loaded ${history.length} messages for ${student.name}`, {
                    studentId: student.id.substring(0, 8) + '***'
                  });
                } else {
                  logDebug(`No messages found for ${student.name}`);
                  successCount++;
                }
              } catch (error) {
                failCount++;
                const err = error;
                logWarn(`Failed to load conversation for ${student.name}`, {
                  studentId: student.id.substring(0, 8) + '***',
                  error: err?.message || String(error),
                  errorCode: err?.code
                });
              }
            })
          );
        }

        // Force reactivity update for messages
        messages.value = { ...messages.value };
        students.value = [...students.value];
        
        logInfo(`📊 Initial conversation loading complete`, {
          total: studentsList.length,
          success: successCount,
          failed: failCount,
          loadedConversations: Object.keys(messages.value).length
        });
      } catch (error) {
        logError("Error in loadInitialConversations", { error });
      }
    };

    // Function to refresh conversations
    const refreshConversations = async () => {
      try {
        if (!currentUser.value) return;

        // Get updated list of students who have active sessions with this tutor
        const studentsList = await messagingPermissionService.getTutorMessagingContacts(
          currentUser.value.uid
        );
        
        // Filter out self and respect explicitly read conversations when refreshing
        students.value = studentsList
          .filter((student) => student.id !== currentUser.value.uid) // Prevent messaging oneself
          .map((student) => ({
            ...student,
            // If conversation was explicitly marked as read, keep it at 0, otherwise use student's count
            unreadCount: messagingStore.explicitlyRead.has(student.id.toLowerCase()) ? 0 : (student.unreadCount || 0)
          }));

        logInfo("🔄 Refreshed conversations with explicitly read filter", {
          totalStudents: studentsList.length,
          markedAsRead: studentsList.filter(s => messagingStore.explicitlyRead.has(s.id.toLowerCase())).length
        });

        // Load conversation history for all students
        await loadInitialConversations(students.value);
      } catch (error) {
        logWarn("Error refreshing conversations", error);
      }
    };

    // Fast initial presence loading using CometChat's native batch status
    const loadInitialPresenceStatus = async () => {
      try {
        // Ensure CometChat is ready
        await ensureCometChatReady();

        if (students.value.length === 0) return;

        // Use batch status retrieval for maximum efficiency
        const studentIds = students.value.map((student) => student.id);
        const statusMap = await getBatchUserStatuses(studentIds);

        // Update students with batch results
        students.value.forEach((student) => {
          student.isOnline = statusMap.get(student.id) || false;
        });

        // Trigger reactivity
        students.value = [...students.value];

        logInfo("✅ Fast batch presence status loaded for all students", {
          totalStudents: students.value.length,
          onlineStudents: students.value.filter((s) => s.isOnline).length,
        });
      } catch (error) {
        logError("Error loading initial presence status", error);
      }
    };

    // Global flag to prevent multiple CometChat initializations
    let isCometChatInitializing = false;
    let cometChatInitPromise = null;

    // Helper function to ensure CometChat is initialized (with deduplication)
    const ensureCometChatReady = async () => {
      // If already initializing, wait for that to complete
      if (isCometChatInitializing && cometChatInitPromise) {
        logDebug("CometChat initialization already in progress, waiting...");
        return await cometChatInitPromise;
      }

      // Check if already logged in
      try {
        const loggedInUser = await getLoggedInUser();
        if (loggedInUser) {
          logDebug("CometChat already initialized and logged in");
          return loggedInUser;
        }
      } catch (error) {
        // Not logged in, proceed with initialization
      }

      // Verify we have current user data before attempting initialization
      if (!currentUser.value?.uid) {
        logWarn("Cannot initialize CometChat - missing user data", {
          hasUid: !!currentUser.value?.uid
        });
        throw new Error("Missing user data for CometChat initialization");
      }

      // Start initialization
      isCometChatInitializing = true;
      cometChatInitPromise = (async () => {
        try {
          const userName =
            currentUser.value?.firstName && currentUser.value?.lastName
              ? formatTutorName(currentUser.value)
              : `Tutor ${currentUser.value.uid.substring(0, 8)}`;

          logInfo("Initializing CometChat for tutor", {
            uid: currentUser.value.uid.substring(0, 8) + "***",
            userName,
          });

          await ensureInitializedAndLoggedIn(String(currentUser.value.uid), userName);

          // Add longer delay to ensure login state is fully propagated
          // CometChat SDK may need time to update internal state
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Verify login succeeded with retry and re-login attempts
          let verifyLogin = null;
          const maxAttempts = 4;

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
              verifyLogin = await getLoggedInUser();
              if (verifyLogin) {
                logInfo(`✅ CometChat login verified on attempt ${attempt + 1}`, {
                  uid: verifyLogin.getUid().substring(0, 8) + "***"
                });
                break;
              }
              logWarn(`Login verification attempt ${attempt + 1} returned null user - login may not have completed`, {
                attempt: attempt + 1,
                maxAttempts,
                uid: currentUser.value.uid.substring(0, 8) + "***"
              });
            } catch (error) {
              logWarn(`Login verification attempt ${attempt + 1} failed with error`, { 
                error,
                errorMessage: error?.message || String(error),
                attempt: attempt + 1
              });
            }

            // Only retry login if not on last attempt
            if (attempt < maxAttempts - 1) {
              // Retry login to recover if session not established
              try {
                logInfo(`Retrying CometChat login (attempt ${attempt + 2}/${maxAttempts})`);
                await ensureInitializedAndLoggedIn(String(currentUser.value.uid), userName);
                // Longer delay after retry
                await new Promise((resolve) => setTimeout(resolve, 500));
              } catch (retryError) {
                logError('Retry ensureInitializedAndLoggedIn failed during verify loop', { 
                  retryError,
                  errorMessage: retryError?.message || String(retryError),
                  attempt: attempt + 1 
                });
              }

              // Progressive delay between attempts (500ms, 1000ms, 1500ms)
              const delay = 500 * (attempt + 1);
              logDebug(`Waiting ${delay}ms before next verification attempt`);
              await new Promise((resolve) => setTimeout(resolve, delay));
            }
          }

          if (!verifyLogin) {
            logError("❌ CometChat login verification failed after all retries", {
              attempts: maxAttempts,
              uid: currentUser.value.uid.substring(0, 8) + "***"
            });

            // Final hard-recovery: use service helper to ensure init+login, then verify once more
            try {
              logWarn("Attempting hard recovery via ensureInitializedAndLoggedIn");
              const userName =
                currentUser.value?.firstName && currentUser.value?.lastName
                  ? formatTutorName(currentUser.value)
                  : `Tutor ${currentUser.value.uid.substring(0, 8)}`;
              await ensureInitializedAndLoggedIn(String(currentUser.value.uid), userName);
              await new Promise((resolve) => setTimeout(resolve, 500));
              verifyLogin = await getLoggedInUser();
            } catch (hardRecoveryError) {
              logError("Hard recovery failed", {
                error: hardRecoveryError,
                message: hardRecoveryError?.message || String(hardRecoveryError),
              });
            }

            if (!verifyLogin) {
              throw new Error(
                "CometChat login verification failed - user not logged in after retries"
              );
            }
          }

          logInfo("✅ CometChat initialized and verified successfully", {
            uid: verifyLogin.getUid().substring(0, 8) + "***",
            attemptsUsed: maxAttempts
          });
          return verifyLogin;
        } catch (error) {
          logError("❌ CometChat initialization failed", { 
            error, 
            errorMessage: error.message,
            uid: currentUser.value?.uid?.substring(0, 8) + "***"
          });
          throw error;
        } finally {
          isCometChatInitializing = false;
          cometChatInitPromise = null;
        }
      })();

      return await cometChatInitPromise;
    };

    // Load current user and initialize messaging
    onMounted(async () => {
      loading.value = true;

      try {
        // Get current user profile
        try {
          currentUser.value = await getCurrentUserProfile();
        } catch (profileError) {
          logError("Failed to get user profile", profileError);
          throw new Error("Authentication failed. Please refresh the page and try again.");
        }

        if (!currentUser.value) {
          logError("No authenticated user found");
          loading.value = false;
          return;
        }

        logInfo("Loading tutor messaging contacts", { userId: currentUser.value.uid });

        // Use the new messaging permission service to get contacts
        // This will only return students with pending or confirmed sessions
        let messagingContacts;
        try {
          messagingContacts = await messagingPermissionService.getTutorMessagingContacts(
            currentUser.value.uid
          );
        } catch (contactsError) {
          logError("Failed to load messaging contacts", contactsError);
          throw new Error("Failed to load contacts. Please refresh the page and try again.");
        }

        // Filter out self and transform contacts to match the expected format
        students.value = messagingContacts
          .filter((contact) => contact.id !== currentUser.value.uid) // Prevent messaging oneself
          .map((contact) => ({
            id: contact.id,
            name: contact.name,
            subject: contact.sessionInfo?.sessionSubject || "Student",
            avatar: contact.avatar, // Don't set default here - let UserAvatar handle it
            isOnline: contact.isOnline,
            lastMessage: contact.lastMessage,
            lastMessageTime: contact.lastMessageTime,
            // If conversation was explicitly marked as read, start with 0, otherwise use contact's count
            unreadCount: messagingStore.explicitlyRead.has(contact.id.toLowerCase()) ? 0 : (contact.unreadCount || 0),
            canMessage: contact.canMessage,
            reason: contact.reason,
          sessionInfo: contact.sessionInfo,
          // Keep original user data for reference
          studentData: contact.userData,
        }));

        // Show UI immediately with basic contact list
        loading.value = false;
        cometChatLoading.value = true;
        logInfo("Tutor messaging contacts loaded", { 
          contactsCount: students.value.length,
          explicitlyReadCount: messagingStore.explicitlyRead.size,
          contactsMarkedAsRead: students.value.filter(s => s.unreadCount === 0 && messagingStore.explicitlyRead.has(s.id.toLowerCase())).length
        });

        // Initialize CometChat in background with error handling
        try {
          // Check if auth key is available
          const authKey = import.meta.env.VITE_COMETCHAT_AUTH_KEY;
          if (!authKey) {
            throw new Error(
              "VITE_COMETCHAT_AUTH_KEY is not configured. Please check your .env file."
            );
          }

          await ensureCometChatReady();
          logInfo("✅ CometChat ready for tutor messaging");

          // Sync unread counts from CometChat to student contacts
          try {
            const unreadMap = await fetchUnreadMessageCountMap();
            
            // Filter to respect explicitly read conversations
            // Trust localStorage over CometChat - if user explicitly opened a conversation,
            // keep it at 0 even if CometChat shows unread (CometChat might be slow to sync)
            const filteredMap = {};
            for (const [uid, count] of Object.entries(unreadMap)) {
              const studentKey = uid.toLowerCase();
              
              // If this conversation was explicitly marked as read, filter it out completely
              // Don't second-guess - if the user opened it, it's read, period.
              if (messagingStore.explicitlyRead.has(studentKey)) {
                logInfo('✅ Filtering out explicitly read conversation (user opened it)', { 
                  studentId: uid.substring(0, 8) + '***',
                  cometchatCount: count,
                  reasoning: 'Trust store - user explicitly opened this conversation'
                });
                // Don't include in filteredMap - keep it at 0
              } else {
                // Not explicitly read, include in map
                filteredMap[uid] = count;
              }
            }
            
            logInfo('📊 Initial sync counts', {
              totalFromCometChat: Object.keys(unreadMap).length,
              afterFiltering: Object.keys(filteredMap).length,
              explicitlyReadCount: messagingStore.explicitlyRead.size
            });
            
            messagingStore.setUnreadCounts(filteredMap);
            broadcastUnreadCountsUpdate(filteredMap);
            syncUnreadCountsToContacts(filteredMap); // Use filtered map to respect explicitly read conversations
          } catch (unreadError) {
            logWarn("Failed to sync initial unread counts", { error: unreadError });
          }

          // Create CometChat users in background (non-blocking)
          createCometChatUsersInBackground(students.value);

          // Load conversation history for ALL students on first login
          // This ensures messages are ready when user opens any conversation
          logInfo('📥 Loading initial conversation history for all students', { 
            count: students.value.length 
          });
          await loadInitialConversations(students.value);

          // Load initial presence status using CometChat's native features
          loadInitialPresenceStatus();
        } catch (cometchatError) {
          // Extract error details properly
          const error = cometchatError;
          const errorMessage =
            error?.message ||
            (error instanceof Error ? error.message : null) ||
            JSON.stringify(error);
          const errorCode = error?.code || error?.name || "UNKNOWN";

          logError("❌ Failed to initialize CometChat - messaging may be limited", {
            errorMessage,
            errorCode,
            errorName: error?.name,
            errorDetails: error?.details,
            currentUserData: {
              uid: currentUser.value.uid.substring(0, 8) + "***",
              hasFirstName: !!currentUser.value?.firstName,
              hasLastName: !!currentUser.value?.lastName,
              userType: currentUser.value?.userType,
            },
          });
          // Don't throw - allow messaging interface to load
          // User can still see contacts, just may not have real-time features
          cometChatLoading.value = false;
        }

        // Set up presence listener (with error handling)
        try {
          const presenceListenerId = `tutor-presence-${currentUser.value.uid}`;
          addUserListener(
            presenceListenerId,
            (onlineUser) => {
              // User came online
              const student = students.value.find((s) => s.id === onlineUser.getUid());
              if (student) {
                student.isOnline = true;
                students.value = [...students.value]; // Trigger reactivity
                logDebug(`Student ${student.name} came online`);
              }
            },
            (offlineUser) => {
              // User went offline
              const student = students.value.find((s) => s.id === offlineUser.getUid());
              if (student) {
                student.isOnline = false;
                students.value = [...students.value]; // Trigger reactivity
                logDebug(`Student ${student.name} went offline`);
              }
            }
          );
        } catch (presenceError) {
          logError("Failed to set up presence listener", presenceError);
          // Non-critical - messaging will still work without presence
        }

        // Set up message listener (with error handling)
        try {
          const listenerId = `tutor-view-${currentUser.value.uid}`;

          addMessageListener(listenerId, (msg) => {
            try {
              const senderUid = msg?.getSender?.()?.getUid?.();
              const text = msg?.getText?.();
              if (!senderUid || !text) return;

              // Find the student who sent the message (case-insensitive comparison)
              const senderStudent = students.value.find(
                (s) => s.id.toLowerCase() === senderUid.toLowerCase()
              );

              if (senderStudent) {
                // Use the student's ID from our students array to ensure consistent casing
                const studentId = senderStudent.id;

                const now = new Date();
                const timestamp = now.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                });

                const incoming = {
                  id: Date.now(),
                  sender: "student",
                  message: text,
                  timestamp,
                  sentAt: msg?.getSentAt?.() || now.getTime() / 1000, // Store Unix timestamp for date grouping
                  isRead: selectedStudent.value?.id === studentId, // Only mark as read if this student is currently selected
                };

                // Update student's last message info
                senderStudent.lastMessage = text;
                senderStudent.lastMessageTime = timestamp;
                senderStudent.lastMessageTimestamp = now; // Use the actual Date object

                if (!messages.value[studentId]) {
                  messages.value[studentId] = [];
                }
                messages.value[studentId].push(incoming);

                // Force reactivity update for messages - create new array reference
                messages.value[studentId] = [...messages.value[studentId]];
                messages.value = { ...messages.value };

                // Force reactivity update for students array to trigger re-sorting
                students.value = [...students.value];

                // Update unread count if student is not currently selected
                if (!selectedStudent.value || selectedStudent.value.id !== studentId) {
                  senderStudent.unreadCount = (senderStudent.unreadCount || 0) + 1;
                  // Update global unread count for the badge via messaging store
                  messagingStore.handleNewMessage(studentId, text, now.getTime(), senderUid);
                  // Broadcast to other tabs
                  broadcastUnreadCountsUpdate(Object.fromEntries(messagingStore.unreadCounts));
                }

                // Force reactivity update for students array
                students.value = [...students.value];
              }
            } catch (error) {
              logWarn("Error handling incoming message", error);
            }
          });

          window.addEventListener("beforeunload", () => removeMessageListener(listenerId));
        } catch (messageListenerError) {
          logError("Failed to set up message listener", messageListenerError);
          // Critical error - log it but don't crash
        }

        // Auto-select student based on query parameters or first student
        if (route.query.contact && students.value.length > 0) {
          // Find the student by contact ID from query parameters
          const targetStudent = students.value.find(
            (student) => student.id === route.query.contact
          );
          if (targetStudent) {
            await selectStudent(targetStudent);
          } else if (students.value.length > 0) {
            // Fallback to first student if target not found
            await selectStudent(students.value[0]);
          }
        } else if (students.value.length > 0) {
          // Default behavior: always select first student (on all devices)
          await selectStudent(students.value[0]);
        }

        // CometChat initialization complete
        cometChatLoading.value = false;

        logInfo("Tutor messaging initialized successfully", {
          contactsCount: students.value.length,
        });
      } catch (error) {
        logError("Error initializing tutor messaging", error);
      } finally {
        loading.value = false;
      }
    });

    // Set up visibility change listener to refresh conversations when tab becomes visible
    onMounted(() => {
      // Track last visibility time to throttle refreshes
      let lastRefreshTime = Date.now();
      const REFRESH_THROTTLE_MS = 30000; // Only refresh if tab was hidden for 30+ seconds

      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          const now = Date.now();
          const timeSinceLastRefresh = now - lastRefreshTime;
          
          // Only refresh if enough time has passed (prevents refresh on quick tab switches)
          if (timeSinceLastRefresh > REFRESH_THROTTLE_MS) {
            logDebug("Tab became visible after extended period, refreshing conversations");
            lastRefreshTime = now;
            refreshConversations();
          } else {
            logDebug("Tab became visible but refresh throttled", {
              timeSinceLastRefresh: `${Math.round(timeSinceLastRefresh / 1000)}s`
            });
          }
        }
      });
    });

    // Refresh conversations when component is activated (keep-alive cache)
    onActivated(async () => {
      logInfo('📱 Messages view activated (keep-alive), syncing unread counts');
      
      try {
        // Load recently marked read timestamps
        let recentlyMarkedRead = {};
        try {
          const stored = localStorage.getItem('elevateED_recently_marked_read_tutor');
          if (stored) {
            recentlyMarkedRead = JSON.parse(stored);
          }
        } catch {}
        
        const now = Date.now();
        
        // Sync unread counts from CometChat to ensure badge is accurate
        const unreadMap = await fetchUnreadMessageCountMap();
        
        // Filter to respect explicitly read conversations AND recently marked read
        const filteredMap = {};
        for (const [uid, count] of Object.entries(unreadMap)) {
          const studentKey = uid.toLowerCase();
          const isExplicitlyRead = messagingStore.explicitlyRead.has(studentKey);
          const wasRecentlyMarked = recentlyMarkedRead[studentKey] && (now - recentlyMarkedRead[studentKey] < 60000);
          
          // Filter out if explicitly read OR recently marked (within 60 seconds)
          if (!isExplicitlyRead && !wasRecentlyMarked && count > 0) {
            filteredMap[uid] = count;
          } else if (isExplicitlyRead || wasRecentlyMarked) {
            logDebug('🔍 Filtering conversation in onActivated', {
              studentId: uid.substring(0, 8) + '***',
              reason: isExplicitlyRead ? 'explicitly read' : 'recently marked read',
              ageMs: wasRecentlyMarked ? (now - recentlyMarkedRead[studentKey]) : null
            });
          }
        }
        
        // Update store with filtered counts
        messagingStore.setUnreadCounts(filteredMap);
        
        // Sync to local students list
        syncUnreadCountsToContacts(filteredMap);
        
        logInfo('✅ Unread counts synced on activation', {
          totalFromCometChat: Object.keys(unreadMap).length,
          afterFiltering: Object.keys(filteredMap).length,
          badgeCount: messagingStore.unreadConversationCount
        });
      } catch (error) {
        logWarn('Failed to sync unread counts on activation', { error });
      }
    });

    // Clean up event listeners
    onBeforeUnmount(() => {
      document.removeEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          refreshConversations();
        }
      });

      // Clean up presence listener
      if (currentUser.value) {
        const presenceListenerId = `tutor-presence-${currentUser.value.uid}`;
        removeUserListener(presenceListenerId);
      }
    });

    return {
      searchQuery,
      selectedStudent,
      filteredStudents,
      selectedStudentMessages,
      selectStudent,
      goBack,
      sendMessage,
      loading,
      cometChatLoading,
      currentUser,
      students,
      canMessage,
    };
  },
};
</script>
<style scoped>
.tutor-page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url("/tutor-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
}

@media (max-width: 768px) {
  .tutor-page-wrapper {
    background-size: cover;
    background-position: center center;
  }
}
</style>
